import React, { useEffect } from 'react';
import { calculateNormalizedPower } from '../../utils/normalizedPower';
import { calculateWKg } from '../../utils/wkgCalculator';
import { calculateVO2Max } from '../../utils/vo2Utils';
import { calculateTSSWithTerrainCycling, calculateIntensityFactor } from '../../utils/tssCyclingUtils';
import { useTranslation } from '../../i18n';

const CyclingMetricsTable = ({ summary, data, session, onMetricsCalculated }) => {
  const { t } = useTranslation();

  const completed = summary?.completed || {};
  const zones     = data?.zones_target || {};
  const weight    = data?.athlete?.weight || 70;
  const records   = data?.records || [];

  const powerValuesSample = records.map(r => r.power).filter(p => typeof p === 'number' && p > 0);
  console.log('🔋 Valores de potencia válidos:', powerValuesSample.length);

  const calculatedNP  = completed?.normalizedPower ?? calculateNormalizedPower(records);
  const calculatedWKg = completed?.wKg ?? calculateWKg(completed?.avgPower, weight);

  const durationSec  = completed?.duracion ?? session?.total_timer_time ?? 0;
  const distanceKm   = Number(completed?.distancia) || (session?.total_distance ?? 0) / 1000;

  let calculatedVO2 = completed?.vo2max;
  if (!calculatedVO2 && distanceKm > 0 && durationSec > 0) {
    calculatedVO2 = calculateVO2Max(distanceKm, durationSec);
  }

  const calculatedTSSObj = calculateTSSWithTerrainCycling(data);
  const calculatedTSS    = completed?.tss ?? calculatedTSSObj?.tss ?? 'N/A';
  const calculatedIF     = completed?.intensityFactor ?? calculatedTSSObj?.intensityFactor ?? calculateIntensityFactor(session?.avg_heart_rate, zones?.threshold_heart_rate);

  let maxSpeed = 0, sumSpeed = 0, count = 0;
  records.forEach((record) => {
    if (record?.speed !== undefined && record?.speed !== null) {
      const speedKmH = record.speed * 3.6;
      sumSpeed += speedKmH;
      count++;
      if (speedKmH > maxSpeed) maxSpeed = speedKmH;
    }
  });

  const avgSpeed = count > 0 ? (sumSpeed / count).toFixed(2) : '0.00';
  maxSpeed = maxSpeed.toFixed(2);

  console.log('✅ Velocidad promedio calculada:', avgSpeed);
  console.log('✅ Velocidad máxima calculada:', maxSpeed);

  useEffect(() => {
    if (typeof onMetricsCalculated === 'function') {
      onMetricsCalculated({
        normalizedPower: calculatedNP,
        wKg: calculatedWKg,
        vo2max: calculatedVO2,
        tss: calculatedTSS,
        intensityFactor: calculatedIF
      });
    }
  }, [calculatedNP, calculatedWKg, calculatedVO2, calculatedTSS, calculatedIF, onMetricsCalculated]);

  return (
    <div className="section-block">
      <h5 className="text-warning">{t('cycling.metricsTitle')}</h5>
      <table className="table table-bordered">
        <thead className="table-dark">
          <tr>
            <th>{t('cycling.metric')}</th>
            <th>{t('cycling.value')}</th>
            <th>{t('cycling.maxLabel')}</th>
            <th>{t('cycling.unit')}</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>{t('cycling.speed')}</td><td>{avgSpeed}</td><td>{maxSpeed}</td><td>km/h</td></tr>
          <tr><td>{t('cycling.cadence')}</td><td>{summary.metrics?.cadencia?.avg ?? session?.avg_cadence ?? 'N/A'}</td><td>{summary.metrics?.cadencia?.max ?? session?.max_cadence ?? 'N/A'}</td><td>rpm</td></tr>
          <tr><td>{t('cycling.heartRate')}</td><td>{summary.metrics?.ritmo_cardiaco?.avg ?? session?.avg_heart_rate ?? 'N/A'}</td><td>{summary.metrics?.ritmo_cardiaco?.max ?? session?.max_heart_rate ?? 'N/A'}</td><td>bpm</td></tr>
          <tr><td>{t('cycling.temperature')}</td><td>{session?.avg_temperature ?? 'N/A'}</td><td>{session?.max_temperature ?? 'N/A'}</td><td>°C</td></tr>
          <tr><td>{t('cycling.power')}</td><td>{completed?.avgPower ?? session?.avg_power ?? 'N/A'}</td><td>{completed?.maxPower ?? session?.max_power ?? 'N/A'}</td><td>W</td></tr>
          <tr><td>{t('cycling.vo2max')}</td><td>{!isNaN(parseFloat(calculatedVO2)) ? calculatedVO2 : 'N/A'}</td><td>–</td><td>ml/kg/min</td></tr>
          <tr><td>{t('cycling.normalizedPower')}</td><td>{calculatedNP ?? 'N/A'}</td><td>–</td><td>W</td></tr>
          <tr><td>{t('cycling.ftp')}</td><td>{completed?.ftp ?? zones?.functional_threshold_power ?? 'N/A'}</td><td>–</td><td>W</td></tr>
          <tr><td>{t('cycling.intensityFactor')}</td><td>{calculatedIF ? Number(calculatedIF).toFixed(2) : 'N/A'}</td><td>–</td><td>–</td></tr>
          <tr><td>{t('cycling.totalWork')}</td><td>{completed?.totalWork ? (completed.totalWork / 1000).toFixed(2) : 'N/A'}</td><td>–</td><td>kJ</td></tr>
          <tr><td>{t('cycling.wkg')}</td><td>{calculatedWKg ?? 'N/A'}</td><td>–</td><td>W/kg</td></tr>
          <tr><td>{t('cycling.tss')}</td><td>{calculatedTSS}</td><td>–</td><td>pts</td></tr>
          <tr><td>{t('cycling.lthr')}</td><td>{zones?.threshold_heart_rate ?? 'N/A'}</td><td>–</td><td>bpm</td></tr>
          <tr><td>{t('cycling.maxHR')}</td><td>{zones?.max_heart_rate ?? 'N/A'}</td><td>–</td><td>bpm</td></tr>
          <tr><td>{t('cycling.ftpZones')}</td><td>{zones?.functional_threshold_power ?? 'N/A'}</td><td>–</td><td>W</td></tr>
        </tbody>
      </table>
    </div>
  );
};

export default CyclingMetricsTable;
